import React, { useState, useEffect } from 'react';
import { Settings, Check, AlertCircle, Loader, Save, Star, MessageCircle, Clock } from 'lucide-react';
import { getConfigurations, updateConfiguration } from '../../services/api';
import '../Boutiques.css';

export default function LoyaltyParametres() {
  const [points,  setPoints]  = useState(1);
  const [notif,   setNotif]   = useState(true);
  const [expMois, setExpMois] = useState(0);
  const [bonJours,setBonJours]= useState(0);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState({ type: '', text: '' });

  useEffect(() => { load(); }, []);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast({ type: '', text: '' }), 4000);
  };

  const load = async () => {
    try {
      const { data } = await getConfigurations();
      const c = data.data || {};
      if (c.loyalty_points_par_1000_xof !== undefined) setPoints(Number(c.loyalty_points_par_1000_xof));
      if (c.loyalty_notif_whatsapp !== undefined) setNotif(c.loyalty_notif_whatsapp !== false && c.loyalty_notif_whatsapp !== 'false');
      if (c.loyalty_points_expiration_mois !== undefined) setExpMois(Number(c.loyalty_points_expiration_mois) || 0);
      if (c.loyalty_bon_validite_jours !== undefined) setBonJours(Number(c.loyalty_bon_validite_jours) || 0);
    } catch {
      showToast('error', 'Erreur de chargement');
    } finally { setLoading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateConfiguration('loyalty_points_par_1000_xof', {
        valeur: Math.max(0, Number(points) || 0),
        description: 'Points de fidélité gagnés par tranche de 1000 XOF',
      });
      await updateConfiguration('loyalty_notif_whatsapp', {
        valeur: !!notif,
        description: 'Notifier les clients de leurs points/récompenses par WhatsApp',
      });
      await updateConfiguration('loyalty_points_expiration_mois', {
        valeur: Math.max(0, parseInt(expMois, 10) || 0),
        description: 'Expiration des points après X mois sans gain (0 = jamais)',
      });
      await updateConfiguration('loyalty_bon_validite_jours', {
        valeur: Math.max(0, parseInt(bonJours, 10) || 0),
        description: 'Durée de validité des bons de réduction en jours (0 = illimité)',
      });
      showToast('success', 'Paramètres enregistrés');
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="bt-loading"><Loader className="bt-spinner" size={26}/><span>Chargement…</span></div>
  );

  const exemple = Math.floor((10000 / 1000) * (Number(points) || 0));

  return (
    <div className="bt-page">
      {toast.text && (
        <div className={`bt-toast bt-toast--${toast.type}`}>
          {toast.type === 'success' ? <Check size={14}/> : <AlertCircle size={14}/>}
          {toast.text}
        </div>
      )}

      <div className="bt-header">
        <div className="bt-header-left">
          <div className="bt-header-icon"><Settings size={20}/></div>
          <div>
            <h1 className="bt-header-title">Paramètres de fidélité</h1>
            <p className="bt-header-sub">Règle d'attribution des points et notifications WhatsApp</p>
          </div>
        </div>
      </div>

      <div className="bt-content">
        <form onSubmit={save} className="bt-card" style={{ padding: 20, maxWidth: 560 }}>

          <div className="bt-section-label"><Star size={13}/> Attribution des points</div>
          <div className="bt-field" style={{ marginTop: 8 }}>
            <label>Points gagnés par tranche de 1 000 XOF</label>
            <input type="number" min="0" step="0.5" value={points}
              onChange={e => setPoints(e.target.value)}
              style={{ maxWidth: 160 }}/>
            <p className="bt-cell-sub" style={{ marginTop: 6 }}>
              Exemple : une vente de 10 000 XOF rapporte <strong>{exemple} point(s)</strong>.
            </p>
          </div>

          <div className="bt-divider" style={{ margin: '18px 0' }}/>

          <div className="bt-section-label"><MessageCircle size={13}/> Notifications</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, cursor: 'pointer' }}>
            <button type="button" className={`bt-ico-btn ${notif ? 'bt-ico-btn--on' : ''}`}
              onClick={() => setNotif(v => !v)} style={{ width: 'auto', padding: '4px 8px' }}>
              {notif ? 'Activé' : 'Désactivé'}
            </button>
            <span style={{ fontSize: 13 }}>Notifier les clients par WhatsApp (points gagnés, paliers, récompenses)</span>
          </label>
          <p className="bt-cell-sub" style={{ marginTop: 8 }}>
            Réutilise le canal WhatsApp du module Marketing (Publications). En l'absence d'identifiants
            WhatsApp configurés, les envois sont simulés (journalisés côté serveur).
          </p>

          <div className="bt-divider" style={{ margin: '18px 0' }}/>

          <div className="bt-section-label"><Clock size={13}/> Expiration</div>
          <div className="bt-form-row" style={{ marginTop: 10 }}>
            <div className="bt-field">
              <label>Expiration des points (mois d'inactivité)</label>
              <input type="number" min="0" step="1" value={expMois}
                onChange={e => setExpMois(e.target.value)} style={{ maxWidth: 160 }}/>
              <p className="bt-cell-sub" style={{ marginTop: 6 }}>
                0 = jamais. Un client averti par WhatsApp 7 jours avant l'expiration de ses points.
              </p>
            </div>
            <div className="bt-field">
              <label>Validité des bons (jours)</label>
              <input type="number" min="0" step="1" value={bonJours}
                onChange={e => setBonJours(e.target.value)} style={{ maxWidth: 160 }}/>
              <p className="bt-cell-sub" style={{ marginTop: 6 }}>
                0 = illimité. Au-delà, le bon passe « expiré » et n'est plus applicable en caisse.
              </p>
            </div>
          </div>

          <div className="bt-modal-footer" style={{ paddingLeft: 0, paddingRight: 0, marginTop: 18 }}>
            <button type="submit" className="bt-btn-confirm" disabled={saving}>
              {saving ? <><Loader size={13} className="bt-spin"/> Enregistrement…</> : <><Save size={13}/> Enregistrer</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
